/**
 * SUR-R92 clauses 1, 2 and 6, and decision C25, in one place: a zero-grant
 * type is deleted on the spot with no dialog at all; a type with grants
 * opens a plain chooser, whose Archive branch is a non-destructive sheet
 * with an announce checkbox and whose Delete branch is the shared
 * two-button destructive confirm.
 */
import {
  useAwardCustomArchive,
  useAwardCustomDelete,
} from "@sportsmet/contest-sdk/private";
import { Button, Checkbox, confirmDestructive, Dialog } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  AWARDS_ARCHIVE_ANNOUNCE_LABEL,
  AWARDS_ARCHIVE_CONFIRM_LABEL,
  AWARDS_CHOOSER_ARCHIVE_LABEL,
  AWARDS_CHOOSER_CANCEL_LABEL,
  AWARDS_CHOOSER_DELETE_LABEL,
  AWARDS_DELETE_CONFIRM_LABEL,
  awardsArchiveBody,
  awardsArchiveTitle,
  awardsChooserArchiveDescription,
  awardsChooserDeleteDescription,
  awardsChooserTitle,
  awardsDeleteConfirmBody,
  awardsDeleteConfirmTitle,
  type typeAwardKind,
} from "./poolAwardsCopy";
import { isChooserNeeded, type typeAwardTypeRow } from "./poolAwardsModel";

export type typeAwardTypeRemoveFlowProps = {
  poolId: string;
  /** The row the Delete control was pressed on, or null when the flow is idle. */
  target: { row: typeAwardTypeRow; kind: typeAwardKind } | null;
  onDone: () => void;
};

type typeStep = "idle" | "chooser" | "archive";

export const AwardTypeRemoveFlow = ({
  poolId,
  target,
  onDone,
}: typeAwardTypeRemoveFlowProps): ReactNode => {
  const archive = useAwardCustomArchive();
  const del = useAwardCustomDelete();

  const [step, setStep] = useState<typeStep>("idle");
  const [announce, setAnnounce] = useState(false);

  useEffect(() => {
    if (target === null) {
      setStep("idle");
      setAnnounce(false);
      return;
    }
    if (!isChooserNeeded(target.row)) {
      void (async () => {
        const result = await del.mutateAsync({
          poolId,
          awardTypeId: target.row.awardTypeId,
        });
        showApiError(result);
        onDone();
      })();
      return;
    }
    setStep("chooser");
    setAnnounce(false);
  }, [target?.row.awardTypeId]);

  if (target === null || step === "idle") return null;

  const { row, kind } = target;

  async function handleArchive(): Promise<void> {
    const result = await archive.mutateAsync({
      poolId,
      awardTypeId: row.awardTypeId,
      announce,
    });
    showApiError(result);
    onDone();
  }

  async function handleDelete(): Promise<void> {
    const confirmed = await confirmDestructive({
      title: awardsDeleteConfirmTitle(row.name),
      body: awardsDeleteConfirmBody({
        kind,
        grantCount: row.grantCount,
        memberCount: row.memberCount,
      }),
      confirmLabel: AWARDS_DELETE_CONFIRM_LABEL,
    });
    if (!confirmed) {
      onDone();
      return;
    }
    const result = await del.mutateAsync({
      poolId,
      awardTypeId: row.awardTypeId,
    });
    showApiError(result);
    onDone();
  }

  if (step === "chooser") {
    return (
      <Dialog
        open={true}
        onOpenChange={(next) => {
          if (!next) onDone();
        }}
        title={awardsChooserTitle(row.name)}
      >
        <Button
          variant="secondary"
          fullWidth
          multiline
          data-testid="award-remove-archive"
          onClick={() => {
            setStep("archive");
          }}
        >
          <span className="flex flex-col items-start gap-1 text-left">
            <span>{AWARDS_CHOOSER_ARCHIVE_LABEL}</span>
            <span className="text-caption text-ink-secondary">
              {awardsChooserArchiveDescription({
                kind,
                grantCount: row.grantCount,
              })}
            </span>
          </span>
        </Button>
        <Button
          variant="secondary"
          fullWidth
          multiline
          data-testid="award-remove-delete"
          onClick={() => void handleDelete()}
        >
          <span className="flex flex-col items-start gap-1 text-left">
            <span>{AWARDS_CHOOSER_DELETE_LABEL}</span>
            <span className="text-caption text-ink-secondary">
              {awardsChooserDeleteDescription(kind)}
            </span>
          </span>
        </Button>
        <Button
          variant="ghost"
          fullWidth
          onClick={() => {
            onDone();
          }}
        >
          {AWARDS_CHOOSER_CANCEL_LABEL}
        </Button>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(next) => {
        if (!next) onDone();
      }}
      title={awardsArchiveTitle(row.name)}
      footer={
        <Button
          variant="primary"
          data-testid="award-archive-submit"
          onClick={() => void handleArchive()}
        >
          {AWARDS_ARCHIVE_CONFIRM_LABEL}
        </Button>
      }
    >
      <p className="text-body text-ink">
        {awardsArchiveBody({
          kind,
          grantCount: row.grantCount,
          memberCount: row.memberCount,
        })}
      </p>
      <Checkbox
        label={AWARDS_ARCHIVE_ANNOUNCE_LABEL}
        checked={announce}
        onCheckedChange={(next) => {
          setAnnounce(next === true);
        }}
      />
    </Dialog>
  );
};
